import { computed } from "vue";
import { useRest } from "./useRest";
import { useSiisToast } from "./useSiisToast";
import { useStoreCompat } from "./useStoreCompat";
import { useActuacionWizardStore } from "@/store/Pinia/useActuacionWizardStore";
import { useLegalDataStore } from "@/store/Pinia/useLegalDataStore";
import { useUfisStore } from "@/store/Pinia/useUfisStore";
import { useCausaCaratulaStore } from "@/store/Pinia/useCausaCaratulaStore";
import { useDelitosStore } from "@/store/Pinia/useDelitosStore";
import { useJuzgadosStore } from "@/store/Pinia/useJuzgadosStore";
import { useModusOperandiStore } from "@/store/Pinia/useModusOperandiStore";
import { useArticulosStore } from "@/store/Pinia/useArticulosStore";
import { buildActuacionPayload } from "@/pages/newActuacion/buildActuacionPayload";

// Mapeo heurístico de mensajes del backend -> step del wizard.
// Source: application/classes/actuaciones/traits/actuacion_validate.php
// Buscamos por substrings (insensible a mayúsculas) en el orden listado.
const ERROR_STEP_RULES = [
  // personas — específicos primero
  {
    step: "afectados",
    needles: [
      "Denunciante",
      "denunciante",
      "Apellido",
      "Nombre",
      "Dni",
      "NumeroIdentificacion",
      "TipoDocumentoID",
      "Nacionalidad",
      "Sexo",
      "EstadoCivil",
      "FechaDeNacimiento",
      "Instruccion",
      "Profesion",
      "DomicilioSipe",
      "CorreoElectronico",
      "Telefono",
      "PersonaJuridica",
      "Cuit",
      "Denominacion",
      "DomicilioJuridico",
      "Rubro",
      "TelefonoJuridico",
      "ExhibeDocumento",
      "TieneEstudios",
    ],
  },
  { step: "vinculados", needles: ["Acusado", "acusado", "Vinculado", "vinculado"] },

  // fecha/lugar
  {
    step: "fecha",
    needles: [
      "Calle",
      "Departamento",
      "Longitud",
      "Latitud",
      "MomentoFechaHora",
      "FechaCreacion",
      "Fechacreacion",
      "preUbicacion",
      "preMoment",
    ],
  },

  // efectos
  {
    step: "efectos",
    needles: [
      "Efecto",
      "CategoriaID",
      "SubcategoriaID",
      "MarcaID",
      "ModeloID",
      "tipoEfecto",
      "EstadoEfectoActuacionID",
    ],
  },

  // afectación patrimonial
  { step: "afectaciones", needles: ["Afectacion", "AfectacionPatrimonial", "Moneda", "Monto"] },

  // interviniente
  {
    step: "intervinientes",
    needles: ["Interviniente", "Nombreyapellido", "Jerarquia", "Dependencia"],
  },

  // datos legales (carátulas, sitio, juzgado, ufi, fiscal, artículos, modus)
  {
    step: "datosLegales",
    needles: [
      "Caratula",
      "TipocausacaratulaID",
      "Articulo",
      "TipoSitioID",
      "Sitio",
      "Juzgado",
      "Modus",
      "TipoufiID",
      "Ufi",
      "Fiscal",
      "Personalfiscal",
      "Numero de legajo",
      "CausaCaratula",
    ],
  },

  // relato / visualización
  { step: "visualizacion", needles: ["Relato", "relato"] },
];

const classifyError = (msg) => {
  if (!msg || typeof msg !== "string") return "visualizacion";
  const lower = msg.toLowerCase();
  for (const rule of ERROR_STEP_RULES) {
    for (const n of rule.needles) {
      if (lower.includes(n.toLowerCase())) return rule.step;
    }
  }
  return "visualizacion";
};

// Nombre de la diligencia inicial según el tipo de actuación (para el flujo nuevo,
// donde el acta que arma el usuario en el wizard se persiste como primera diligencia).
const actaInicioNombrePorTipo = (tipoKey = "") => {
  if (tipoKey.startsWith("sumario-")) return "ACTA DE INICIO";
  if (tipoKey.startsWith("expediente-")) {
    return tipoKey.endsWith("-oficio")
      ? "ACTA DE INICIO CONTRAVENCIONAL POR OFICIO"
      : "DENUNCIA EXPEDIENTE CONTRAVENCIONAL POR DENUNCIA";
  }
  if (tipoKey.startsWith("ufi-")) return "ACTA DE INICIO LEGAJO UFI";
  if (tipoKey.startsWith("preliminares-")) return "ACTA DE INICIO PRELIMINAR";
  return "ACTA DE INICIO";
};

const buildLegacyDenunciaHtml = (rawHtml, relato = "") => {
  if (!rawHtml || typeof rawHtml !== "string") return "";

  const parser = new DOMParser();
  const doc = parser.parseFromString(rawHtml, "text/html");

  const cleanText = (text = "") =>
    text
      .replace(/\u00a0/g, " ")
      .replace(/[ \t]+/g, " ")
      .replace(/\s([.,;:])/g, "$1")
      .trim();

  const getBlockText = (title) => {
    const blocks = [...doc.querySelectorAll('[data-wizard-block="true"]')];
    const block = blocks.find((b) => b.getAttribute("data-title") === title);
    return cleanText(block?.querySelector(".wizard-block__body")?.textContent || "");
  };

  const mainParagraph = cleanText(doc.querySelector("p")?.textContent || "");
  const cierreParagraphs = [...doc.body.querySelectorAll("p")].map((p) =>
    cleanText(p.textContent || "")
  );
  const cierre = cierreParagraphs[cierreParagraphs.length - 1] || "";

  const denuncianteText = getBlockText("DENUNCIANTE / AFECTADO");

  // Datos del denunciante
  const nombreMatch = denuncianteText.match(/^(.+?),\s*DNI N°/i);
  const dniMatch = denuncianteText.match(/DNI N°\s*([^,]+)/i);

  const nombre = cleanText(nombreMatch?.[1] || "");
  const dni = cleanText(dniMatch?.[1] || "");
  const telefono = ""; // si más adelante lo tenés en otro lado, lo metés acá

  // Bloques extra
  const vinculados = getBlockText("VINCULADOS") || "No hay vinculados cargados";
  const efectos = getBlockText("EFECTOS") || "No hay efectos cargados";
  const fechaHoraUbicacion = getBlockText("FECHA, HORA Y UBICACIÓN");

  // Relato principal: desde ---
  const relatoDesdeGuiones = mainParagraph.includes("---")
    ? mainParagraph.slice(mainParagraph.indexOf("---"))
    : mainParagraph;

  const relatoText = relato || getBlockText("RELATO");

  return `
<p align="center" style="line-height: 1.4em; color: black; text-indent: 0em; text-align: center; width: 100%; display: block;">
  <strong><u>DENUNCIA</u></strong>
</p>
<p style="line-height: 1.4em; color: black; text-indent: 0em;">
  <strong>DENUNCIANTE:&nbsp;</strong>${nombre}.
</p>
<p style="line-height: 1.4em; color: black; text-indent: 0em;">
  <strong>D.N.I Nº:&nbsp;</strong>${dni}.
</p>
<p style="line-height: 1.4em; color: black; text-indent: 0em;">
  <strong>Tel No::</strong> ${telefono}.
</p>
<p style="line-height: 1.4em; color: black; text-indent: 0em; text-align: justify;">
  ${relatoDesdeGuiones}
</p>
${
  relatoText
    ? `<p style="line-height: 1.4em; color: black; text-indent: 0em; text-align: justify;">
         ${relatoText}
       </p>`
    : ""
}
<p style="line-height: 1.4em; color: black; text-indent: 0em; text-align: justify;">
  ${vinculados}
</p>
<p style="line-height: 1.4em; color: black; text-indent: 0em; text-align: justify;">
  ${efectos}
</p>
${
  fechaHoraUbicacion
    ? `<p style="line-height: 1.4em; color: black; text-indent: 0em; text-align: justify;">
         ${fechaHoraUbicacion}
       </p>`
    : ""
}
${
  cierre && cierre !== mainParagraph
    ? `<p style="line-height: 1.4em; color: black; text-indent: 0em; text-align: justify;">
         ${cierre}
       </p>`
    : ""
}
`.trim();
};

const buildDiligenciaInicial = ({ actuacionId, tipoKey, actaDoc, userId, relato = "" }) => {
  const html =
    (typeof actaDoc?.html === "string" && actaDoc.html) ||
    (typeof actaDoc?.text === "string" && actaDoc.text) ||
    "";
  return {
    ActuacionID: actuacionId,
    Nombre: actaInicioNombrePorTipo(tipoKey),
    Modificada: "false",
    UsuarioID: userId || "",
    Contenido: buildLegacyDenunciaHtml(html, relato),
    Fechainivig: new Date()
      .toLocaleString("es-AR", {
        timeZone: "America/Argentina/Buenos_Aires",
        year: "2-digit",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
      .replace(/\//g, "-")
      .replace(",", ""),
  };
};

// El backend puede anteponer salida de var_dump/echo al JSON real.
// Aceptamos: objeto ya parseado, o string con JSON al final.
const parseCreateResponse = (raw) => {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return {};
  try {
    return JSON.parse(raw);
  } catch (e) {
    const start = raw.lastIndexOf("{");
    const end = raw.lastIndexOf("}");
    if (start === -1 || end === -1 || end <= start) return {};
    // retrocedemos buscando el primer "{" cuyo JSON parsee correcto
    for (let i = start; i >= 0; i = raw.lastIndexOf("{", i - 1)) {
      const slice = raw.slice(i, end + 1);
      try {
        return JSON.parse(slice);
      } catch (err) {
        /* seguir retrocediendo */
      }
      if (i === 0) break;
    }
    return {};
  }
};

const groupErrorsByStep = (errors = []) => {
  const map = {};
  const list = Array.isArray(errors) ? errors : [];
  for (const raw of list) {
    const msg = typeof raw === "string" ? raw : raw?.message || JSON.stringify(raw);
    const step = classifyError(msg);
    if (!map[step]) map[step] = [];
    map[step].push(msg);
  }
  return map;
};

export function useActuacionSubmit() {
  const wizard = useActuacionWizardStore();
  const legalStore = useLegalDataStore();
  const ufisStore = useUfisStore();
  const caratulasStore = useCausaCaratulaStore();
  const delitosStore = useDelitosStore();
  const juzgadosStore = useJuzgadosStore();
  const modusStore = useModusOperandiStore();
  const articulosStore = useArticulosStore();
  const rest = useRest();
  const toast = useSiisToast();
  const store = useStoreCompat();

  const submitting = computed(() => wizard.submitting);

  const collectCatalogs = () => {
    const caratulas = caratulasStore.causasCaratulas || store.state.siis?.tiposCausaCaratula || [];
    const delitos = delitosStore.delitos || [];
    // Para UFIs el dropdown de "Delitos" escribe en formData.causasCaratulas, pero los IDs
    // pertenecen al catálogo de delitos. Unificamos ambos para que el builder pueda
    // resolverlos sin importar el tipo de actuación.
    return {
      ufis: ufisStore.ufis || [],
      tiposCausaCaratula: [...caratulas, ...delitos],
      juzgados: juzgadosStore.allJuzgados || store.state.siis?.juzgados || [],
      modusOperandi: modusStore.modusOperandi || store.state.siis?.modusOperandi || [],
      jerarquias: store.state.siis?.jerarquia || [],
      articulos: articulosStore.articulos || [],
    };
  };

  const submit = async () => {
    if (wizard.submitting) return { ok: false, reason: "already-submitting" };

    wizard.clearStepErrors();
    wizard.setSubmitError(null);
    wizard.setSubmitting(true);

    try {
      if (!wizard.type) throw new Error("Falta el tipo de actuación");

      // Capturamos antes de reset: los necesitamos para crear la diligencia inicial.
      const tipoKey = wizard.type;
      const actaDocSnapshot = wizard.actaDoc;
      const relatoSnapshot = wizard.relato || "";

      const payload = buildActuacionPayload(wizard, {
        catalogs: collectCatalogs(),
        user: store.state?.user || null,
      });

      const response = await rest.post({
        url: "/actuacion",
        formData: payload,
        success: "Actuación creada correctamente",
      });

      // El backend a veces filtra var_dump/echo antes del JSON final; si axios no
      // pudo parsear, intentamos extraer el último objeto JSON del string.
      const data = parseCreateResponse(response?.data);
      const nuevoId =
        data?.ActuacionID || data?.actuacionID || data?.data?.ActuacionID || data?.id || null;

      wizard.setCreatedActuacionId(nuevoId);

      // Crear diligencia inicial con el acta armada en el wizard.
      // No bloquea el redirect: si falla, se loguea y el usuario podrá cargarla desde el detalle.
      if (nuevoId) {
        try {
          const diligenciaPayload = buildDiligenciaInicial({
            actuacionId: nuevoId,
            tipoKey,
            actaDoc: actaDocSnapshot,
            userId: store.state?.user?.uid || store.state?.user?.UsuarioID || "",
            relato: relatoSnapshot,
          });
          await rest.post({ url: "/diligencia/", formData: diligenciaPayload });
        } catch (diligErr) {
          console.error("No se pudo crear la diligencia inicial", diligErr);
        }
      }

      legalStore.reset();
      wizard.reset();

      // Nota: el redirect queda a cargo del caller (NewActuacion.vue) para
      // poder mostrar un modal de éxito antes de navegar.
      return { ok: true, id: nuevoId, data };
    } catch (err) {
      const resp = err?.response || err;
      const body = resp?.data || {};
      const status = resp?.status;

      if (status === 422 && Array.isArray(body.error)) {
        const grouped = groupErrorsByStep(body.error);
        wizard.setStepErrors(grouped);
        wizard.setSubmitError({
          status,
          error: body.error,
          errorContent: body.errorContent,
        });
        toast.open({
          message: "Hay errores de validación. Revisá los pasos marcados en rojo.",
          type: "error",
          position: "top-right",
          duration: 5000,
        });
      } else {
        wizard.setSubmitError({
          status: status || 0,
          error: body?.error || err?.message || "Error desconocido",
        });
      }

      return { ok: false, status, error: body?.error || err };
    } finally {
      wizard.setSubmitting(false);
    }
  };

  return {
    submit,
    submitting,
    classifyError,
  };
}

export default useActuacionSubmit;
